'use client';

import * as React from 'react';

import { CheckIcon } from '@radix-ui/react-icons';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 DIE MARKE IST 16 px, DER KASTEN MOBIL 44 px (24.09.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Blatt A2 setzt die Grenze und sagt im selben Atemzug, dass Bild und Fläche
 * auseinanderfallen dürfen: „Knöpfe dürfen 38 px hoch aussehen, brauchen aber
 * 44 px Trefferfläche." Blatt M zieht sie am Telefon durch: „Jede Fläche
 * mindestens 44 px hoch" — und nimmt die Folge für lange Listen ausdrücklich in
 * Kauf: „Die Teilnehmer stehen als 72-px-Zeilen mit großen Kästchen da."
 *
 * Deshalb trägt die Radix-Wurzel die Höhe und ein inneres `<span>` die
 * sichtbare Marke. Genau die Aufteilung, die B-6 verlangt: „er braucht 44 px
 * Höhe und darf INNEN KLEINER AUSSEHEN" — und zwar am KASTEN, nicht an einem
 * Pseudo-Element, das der Wächter zu Recht nicht zählt.
 *
 * 🔴 WARUM HIER UND NICHT AN DEN AUFRUFSTELLEN: gemessen am 24.09.2026 gibt es
 * 50 Dateien mit `<Checkbox`, 53 mit `<Switch`, 15 mit `<RadioGroupItem` — und
 * KEINE davon setzt eine eigene Höhe (nur `apps/dev-tool`s Schaubild). Der
 * Wächter `S9d` meldete die rohen Kästchen quer durch die App, nicht eine
 * einzelne Fläche.
 *
 * ⚠️ WAS DAS NICHT LÖST — und was der Vorbefund dazu ist: ein Klick auf die
 * BESCHRIFTUNG neben dem Kästchen schaltet NICHT. Gemessen gegen staging am
 * 24.09.2026 an drei Stellen des Vorlagen-Generators (Element 16 px, Label
 * 20 px, `aria-checked` vorher = nachher). Ursache: Radix rendert hier
 * `<button role="checkbox">`, und ein `<button>` ist kein beschriftbares
 * Element — die implizite `<label>`-Bindung greift nicht, und den
 * `BubbleInput` legt Radix nur im Formular-Kontext an (gemessen: keiner da).
 * Eine 44 px hohe Label-Zeile wäre also eine Fläche, die nichts tut. Das ist
 * ein eigener Vorgang, kein Teil dieser Änderung.
 *
 * ⚠️ Am Rechner (`md:`) ändert sich NICHTS — DS-Regel 6 („DS-Änderungen sind
 * additiv"). Mobil wächst die Zeile, und das ist die beauftragte Wirkung.
 */
const Checkbox: React.FC<
  React.ComponentPropsWithRef<typeof CheckboxPrimitive.Root>
> = ({ className, ...props }) => (
  <CheckboxPrimitive.Root
    className={cn(
      /*
       * Am Telefon 44 px Tippfläche (M1), aber nur 16 px im Fluss: der
       * negative Rand nimmt die 28 px wieder heraus. Vorher stand das
       * Kästchen in einer Zeile mit `items-start` 14 px unter der ersten
       * Textzeile (Registrieren, Nutzungsbedingungen — Sichtlauf 06.10.2026).
       */
      'peer group -my-3.5 flex h-11 w-4 shrink-0 items-center justify-center focus-visible:outline-hidden disabled:cursor-not-allowed md:my-0 md:h-4',
      className,
    )}
    {...props}
  >
    <span
      className={cn(
        // #1329 T19b: gewählt = Akzent (subcontracts `accent-[#FF3D00]`, vertraege/page.tsx:177),
        // leer = Rahmen in muted-2 (5,75:1 auf Weiss), Fokus 2 px primary mit Abstand.
        'border-muted-2 group-focus-visible:ring-ring group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary group-data-[state=checked]:text-primary-foreground group-disabled:border-muted group-disabled:bg-muted rounded-kennmarke flex h-4 w-4 items-center justify-center border group-focus-visible:ring-2 group-focus-visible:ring-offset-2',
      )}
    >
      <CheckboxPrimitive.Indicator
        className={cn('flex items-center justify-center text-current')}
      >
        <CheckIcon className="h-4 w-4" />
      </CheckboxPrimitive.Indicator>
    </span>
  </CheckboxPrimitive.Root>
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
